/**
 * Profile Service
 * Handles profile-related API calls
 */

import { getSocket, encode, decode, C, storage, getUID } from '@/utils';

/**
 * Fetch user info via socket
 * Returns a promise that resolves when user info is received
 */
export const fetchUserInfo = () => {
  return new Promise((resolve, reject) => {
    const uid = getUID();
    const currentSocket = getSocket();
    
    if (!uid || !currentSocket) {
      reject(new Error('User ID or socket not available'));
      return;
    }

    const timeout = setTimeout(() => {
      currentSocket.off(C.USER_INFO, handleUserInfo);
      reject(new Error('User info request timeout'));
    }, 10000); // 10 second timeout

    const handleUserInfo = (data) => {
      try {
        clearTimeout(timeout);
        currentSocket.off(C.USER_INFO, handleUserInfo);
        const decodedData = decode(data);
        if (decodedData && decodedData.status) {
          resolve(decodedData);
        } else {
          reject(new Error('Invalid user info data'));
        }
      } catch (error) {
        clearTimeout(timeout);
        currentSocket.off(C.USER_INFO, handleUserInfo);
        reject(error);
      }
    };

    currentSocket.on(C.USER_INFO, handleUserInfo);
    
    // Request user info
    currentSocket.emit(C.USER_INFO, encode({
      id: uid,
      coin: storage.getKey('coin') || '',
      rate: null,
      game: 'all',
      first: true,
    }));
  });
};

/**
 * Fetch bet and win counts
 */
export const fetchBetWinCount = async () => {
  try {
    const uid = getUID();
    if (!uid) {
      throw new Error('User ID not available');
    }

    const response = await fetch(`https://api.jackopot.com/betHistory/user/${uid}/bet-win-count`);
    if (!response.ok) {
      throw new Error('Failed to fetch bet and win counts');
    }
    const data = await response.json();
    return {
      betCount: data.betCount || 0,
      winCount: data.winCount || 0
    };
  } catch (error) {
    console.error('Error fetching bet and win counts:', error);
    throw error;
  }
};

/**
 * Fetch wager check data
 */
export const fetchWagerCheck = async () => {
  try {
    const uid = getUID();
    if (!uid) {
      throw new Error('User ID not available');
    }

    const response = await fetch(`https://api.jackopot.com/api/deposits/check-wager/${uid}`);
    if (!response.ok) {
      throw new Error('Failed to fetch wager check data');
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching wager check:', error);
    throw error;
  }
};
